import React, { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";

import {
  Pen,
  FileEarmarkText,
  GeoAlt,
  Calendar3,
  Envelope,
  ArrowCounterclockwise,
  Person,
  CheckCircle,
  XCircle,
  CheckCircleFill,
} from "react-bootstrap-icons";

import "./PayoffSignature.css";
import urls from "../../Utils/urls";
import axiosInstance from "../../axios";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { oracleConfig } from "../../Utils/constant";
import { LoadingButton } from "../../Utils/CustomComponent";
import PopupWrapper from "../../components/PopupWrapper";
import { toast } from "react-toastify";
import { BsChevronLeft } from "react-icons/bs";

const AUTHORIZATION_TEXT = (
  <>
    I authorize the third party listed above to request a payoff statement for
    my loan on my behalf from Superior Loan Servicing.
  </>
);

const AUTHORIZATION_TEXT2 = (
  <>
    <strong>
      You may revoke this authorization at any time by providing written notice
      to Superior Loan Servicing.
    </strong>{" "}
    I/We hereby indemnify and forever hold Superior Loan Servicing harmless from
    any and all actions and causes of actions, suits, claims, attorney’s fees,
    or demands against Superior Loan Servicing, which I/we and/or my/our heirs
    may have resulting from Superior Loan Servicing discussing, or declining to
    discuss, my/our account with the above-named requestor or person identifying
    himself/herself to be that requestor, or resulting from providing, or
    declining to provide, any documents or other information concerning the
    account to the requestor.
  </>
);

const AUTHORIZATION_TEXT_PLAIN =
  "I authorize the third party listed above to request a payoff statement for my loan on my behalf from Superior Loan Servicing. You may revoke this authorization at any time by providing written notice to Superior Loan Servicing. I/We hereby indemnify and forever hold Superior Loan Servicing harmless from any and all actions and causes of actions, suits, claims, attorney’s fees, or demands against Superior Loan Servicing, which I/we and/or my/our heirs may have resulting from Superior Loan Servicing discussing, or declining to discuss, my/our account with the above-named requestor or person identifying himself/herself to be that requestor, or resulting from providing, or declining to provide, any documents or other information concerning the account to the requestor.";

const RequestDetails = () => {
  const signatureRef = useRef(null);

  const [decision, setDecision] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);
  const [rejectionRemark, setRejectionRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestDetails, setRequestDetails] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  // const { isLargeDevice } = useSelector((state) => state.RS_Sidebar);
  const email = useSelector(
    (state) => state.RS_Authentication.user.details.email,
  );
  const masqueradeEmail = useSelector(
    (state) => state.RS_Authentication.masquerade,
  );
  const location = useLocation();
  const navigate = useNavigate();

  const thirdPartyEmail = localStorage.getItem("email_for_payoff");
  const isThirdParty =
    location?.state?.source && location?.state?.source === "third-party";
  const isExternalBorrower =
    location?.state?.source &&
    location?.state?.source === "external-borrower" &&
    location.pathname === "/payoff-borrower-authorization";
  const loan_number = location?.state?.loan_number;
  const property_address = location?.state?.property_address;
  const payoff_good_through_date = location?.state?.payoff_good_through_date;
  const demand_expiration_date = location?.state?.demand_expiration_date;
  const requestor_email = location?.state?.requestor_email;
  const requestor_name = location?.state?.requestor_name;
  const request_id = location?.state?.request_id;
  const loanrecid = location?.state?.loanrecid;

  const isAuthorizationFlow = decision === "authorize";
  const isRejectionFlow = decision === "reject";

  useEffect(() => {
    if (request_id) {
      getRequestDetails();
    }
  }, [request_id]);

  useEffect(() => {
    if (isSubmitted) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            clearInterval(timer);
            if (isExternalBorrower) {
              navigate("/authorize-requests", {
                state: {
                  authorizedBorrower: true
                }
              });
            } else {
              navigate("/payoff-auth-requests");
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [navigate, isSubmitted]);

  async function getRequestDetails() {
    try {
      const res = await axiosInstance.post(
        urls.payOffDemand.thirdPartyPayoffs,
        {
          loanrecid: loanrecid,
        },
        oracleConfig,
      );

      const data = res?.data?.data?.[0];
      setRequestDetails(data);
    } catch (error) {
      console.error(error);
    }
  }

  const clearSignature = () => {
    signatureRef.current?.clear();
    setSignatureEmpty(true);
  };

  const handleSignatureEnd = () => {
    if (signatureRef.current) {
      setSignatureEmpty(signatureRef.current.isEmpty());
    }
  };

  const handleDecisionChange = (value) => {
    setDecision(value);

    if (value === "reject") {
      setAuthorized(false);
      clearSignature();
    }

    if (value === "authorize") {
      setRejectionRemark("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --------------------------------
    // No decision selected
    // --------------------------------

    if (!decision) {
      alert("Please choose whether to authorize or reject the payoff request.");
      return;
    }

    // --------------------------------
    // REJECTION FLOW
    // --------------------------------

    if (isRejectionFlow) {
      if (!rejectionRemark.trim()) {
        alert("Please enter a remark for rejecting the payoff request.");
        return;
      }

      setSubmitting(true);

      try {
        const payload = {
          request_id: request_id,
          remark: rejectionRemark,
          rejected_by: email,
        };

        const response = await axiosInstance.post(
          urls.payOffDemand.authRejection,
          payload,
          oracleConfig,
        );

        toast.success(
          "Payoff Authorization request has been rejected successfully!",
        );
        navigate(-1);
      } catch (error) {
        console.error("Payoff rejection submission failed:", error);
        alert("Unable to reject the payoff request.");
      } finally {
        setSubmitting(false);
      }

      return;
    }

    // --------------------------------
    // AUTHORIZATION FLOW
    // --------------------------------

    if (isAuthorizationFlow) {
      if (!authorized) {
        alert("Please authorize the payoff request before signing.");
        return;
      }

      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        alert("Please provide your signature.");
        return;
      }

      setSubmitting(true);

      try {
        const payload = {
          loan_number,
          property_address,
          payoff_good_through_date,
          demand_expiration_date,
          requestor_email,
          requestor_name,
          decision: "authorized",
          authorization_accepted: authorized,
          signature: signatureRef.current.toDataURL("image/png"),
          rejection_remark: null,
          street: requestDetails?.street,
          city: requestDetails?.city,
          state: requestDetails?.state,
          zipcode: requestDetails?.zipcode,
          fullName: requestDetails?.borrower_fullname,
        };

        const response = await axiosInstance.post(
          urls.payOffDemand.genarateSignaturePdf,
          payload,
          {
            ...(isExternalBorrower && {
              headers: {
                "x-api-key": `12kXk3Xiou6Z50k0HSFZ5D8CScq2w6xJThdasldkfjaltht33223Dktkslsk`,
              },
            }),
            responseType: "blob",
          },
        );

        // const blob = new Blob([response.data], {
        //   type: "application/pdf",
        // });
        const pdfBlob = response.data;

        const pdfFile = new File(
          [pdfBlob],
          `payoff-authorization-${loan_number}.pdf`,
          { type: "application/pdf" },
        );

        // const url = window.URL.createObjectURL(blob);

        // const link = document.createElement("a");
        // link.href = url;

        // link.download = `payoff-request-${loan_number}.pdf`;

        // document.body.appendChild(link);
        // link.click();

        // link.remove();

        // window.URL.revokeObjectURL(url);
        if (pdfBlob) {
          const formData = new FormData();
          formData.append("document", pdfFile, pdfFile.name);
          formData.append("request_id", request_id);

          const bucketResponse = await axiosInstance.post(
            urls.payOffDemand.uploadBorrowerAuth,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            },
          );

          if (bucketResponse?.status == 200) {
            const authPayload = {
              request_id: bucketResponse?.data?.request_id,
              filename: bucketResponse?.data?.filename,
              file_mimetype: bucketResponse?.data?.file_mimetype,
              url: bucketResponse?.data?.url,
              email: isExternalBorrower ? thirdPartyEmail : email,
              loanrecid: loanrecid,
              logger_email: isExternalBorrower
                ? thirdPartyEmail
                : masqueradeEmail?.isIt == true
                  ? masqueradeEmail?.p_logged_in_email
                  : email,
            };

            const finalApiRes = await axiosInstance.post(
              urls.payOffDemand.uploadSignatureInfo,
              authPayload,
              oracleConfig,
            );

            if (finalApiRes?.status == 200) {
              setIsSubmitted(true);
            }
          }
        }
      } catch (error) {
        console.error("Payoff submission failed:", error);
        alert("Unable to submit payoff request.");
      } finally {
        setSubmitting(false);
      }
    }
  };

  function handleCancel() {
    setIsSubmitted(false);
    navigate(-1);
  }

  if (!request_id) {
    return <Navigate to="unauthorized" replace />;
  }

  return (
    <div
      className="payoff-page"
      style={isThirdParty || isExternalBorrower ? { paddingTop: "6rem" } : null}
    >
      <div
        className={`mb-4 text-secondary small ${isThirdParty || isExternalBorrower ? "text-start" : ""}`}
        style={{
          marginLeft:
            isThirdParty || isExternalBorrower ? "calc(200px)" : "0px",
        }}
      >
        <span
          onClick={() => navigate(-1)}
          style={{ cursor: "pointer" }}
          className="linkClick"
        >
          <BsChevronLeft className="me-1" /> Back{" "}
        </span>
      </div>
      {!isSubmitted ? (
        <div
          className={`payoff-card ${isThirdParty || isExternalBorrower ? "text-start custom-payoff-card-width" : ""}`}
        >
          {/* -------------------------------- HEADER -------------------------------- */}

          <div className="payoff-header">
            <div className="payoff-header-icon">
              <Pen size={34} />
            </div>

            {isThirdParty ? (
              <div>
                <h1>Payoff Request Details</h1>
              </div>
            ) : location?.state?.status === "PENDING" &&
              (location?.state?.source === "borrower" ||
                location?.state?.source === "external-borrower") ? (
              <div>
                <h1>Sign the Third-Party Authorization Request</h1>

                <p>
                  The undersigned Borrower and Co-Borrower (if any), authorize
                  Superior Loan Servicing and its authorized representatives to
                  release, furnish, provide, exchange, and request information
                  related to my/our loan.
                </p>
              </div>
            ) : (
              location?.state?.status !== "PENDING" &&
              (location?.state?.source === "borrower" ||
                location?.state?.source === "external-borrower") && (
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-4">
                  <div>
                    <h1>Third-Party Authorization Request</h1>

                    <p>
                      The undersigned Borrower and Co-Borrower (if any),
                      authorize Superior Loan Servicing and its authorized
                      representatives to release, furnish, provide, exchange,
                      and request information related to my/our loan.
                    </p>
                  </div>
                </div>
              )
            )}
            {location?.state?.status && (
              <span
                className={`badge px-5 py-3 ${
                  location?.state?.status === "PENDING"
                    ? "bg-info"
                    : location?.state?.status === "EXPIRED"
                      ? "bg-error"
                      : "bg-success"
                }`}
              >
                {location?.state?.status}
              </span>
            )}
          </div>

          <div className="payoff-divider" />

          {/* -------------------------------- DETAILS -------------------------------- */}

          <div className="payoff-details-grid">
            {/* Loan Number */}
            <div className="payoff-field">
              <label>Loan Number</label>

              <div className="payoff-input readonly">
                <FileEarmarkText size={25} />
                <span>{loan_number}</span>
              </div>
            </div>

            {/* Property Address */}
            <div className="payoff-field">
              <label>Property Address</label>

              <div className="payoff-input readonly address-field">
                <GeoAlt size={25} />
                <span>{property_address}</span>
              </div>
            </div>

            {/* Payoff Good Through */}
            <div className="payoff-field">
              <label>Payoff Good Through Date</label>

              <div className="payoff-input readonly">
                <Calendar3 size={24} />
                <span>{payoff_good_through_date}</span>
              </div>
            </div>

            {/* Demand Expiration */}
            <div className="payoff-field">
              <label>Demand Expiration Date</label>

              <div className="payoff-input readonly">
                <Calendar3 size={24} />
                <span>{demand_expiration_date}</span>
              </div>
            </div>

            {/* Requestor Email */}
            <div className="payoff-field">
              <label>Requestor Email</label>

              <div className="payoff-input readonly">
                <Envelope size={25} />
                <span>{requestor_email}</span>
              </div>
            </div>

            {/* Requestor Name */}
            <div className="payoff-field">
              <label>Requestor Name</label>

              <div className="payoff-input readonly">
                <Person size={24} />
                <span>{requestor_name}</span>
              </div>
            </div>
          </div>

          {/* -------------------------------- DECISION -------------------------------- */}

          {(location.pathname === "/payoff-authorization" ||
            location.pathname === "/payoff-borrower-authorization") &&
            location?.state?.status === "PENDING" && (
              <div className="payoff-decision-section">
                <h2>Response to Payoff Request</h2>

                <p>Please select an option below to continue.</p>

                <div className="payoff-decision-options">
                  {/* AUTHORIZE */}
                  <button
                    type="button"
                    className={`decision-card ${
                      isAuthorizationFlow ? "active authorize" : ""
                    }`}
                    onClick={() => {
                      handleDecisionChange("authorize");
                      setShowAuthModal(true);
                    }}
                    disabled={submitting}
                  >
                    <div className="decision-icon">
                      <CheckCircle size={24} />
                    </div>

                    <div className="decision-content">
                      <strong>Authorize Request</strong>

                      <span>
                        Approve the third-party payoff request and provide your
                        signature.
                      </span>
                    </div>
                  </button>

                  {/* REJECT */}
                  <button
                    type="button"
                    className={`decision-card ${
                      isRejectionFlow ? "active reject" : ""
                    }`}
                    onClick={() => {
                      handleDecisionChange("reject");
                      setShowRejectionModal(true);
                    }}
                    disabled={submitting}
                  >
                    <div className="decision-icon">
                      <XCircle size={24} />
                    </div>

                    <div className="decision-content">
                      <strong>Reject Request</strong>

                      <span>
                        Decline the third-party payoff request and provide a
                        reason.
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}

          {/* -------------------------------- AUTHORIZATION FLOW -------------------------------- */}

          {isAuthorizationFlow && (
            <ActionModal
              title={"Authorize Request"}
              show={showAuthModal}
              setShow={setShowAuthModal}
              comp={
                <>
                  {/* Authorization */}
                  <div className="authorization-wrapper">
                    <label className="authorization-checkbox">
                      <input
                        type="checkbox"
                        checked={authorized}
                        onChange={(e) => setAuthorized(e.target.checked)}
                      />

                      <span className="custom-checkbox">
                        {authorized && "✓"}
                      </span>

                      <span className="authorization-text">
                        {AUTHORIZATION_TEXT}
                        <span className="required-star">*</span>
                      </span>
                    </label>
                  </div>

                  {/* Signature */}
                  <div className="signature-section">
                    <h2>Your Signature</h2>

                    <p>Please sign below using the signature pad.</p>

                    <div className="signature-pad-wrapper">
                      <SignatureCanvas
                        ref={signatureRef}
                        penColor="#152b4b"
                        backgroundColor="#fdfefe"
                        canvasProps={{
                          className: "signature-pad",
                        }}
                        onEnd={handleSignatureEnd}
                      />

                      <div className="signature-line" />

                      {signatureEmpty && (
                        <div className="signature-placeholder">
                          <Pen size={38} />
                          <span>Sign here</span>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="clear-signature-btn"
                      onClick={clearSignature}
                      disabled={signatureEmpty || submitting}
                    >
                      <ArrowCounterclockwise size={22} />
                      Clear Signature
                    </button>
                  </div>

                  <div
                    style={{
                      marginTop: "14px",
                      padding: "11px 13px",
                      borderLeft: "3px solid #d8dee8",
                      backgroundColor: "#f8fafc",
                      borderRadius: "0 6px 6px 0",
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        color: "#718096",
                        fontSize: "11.5px",
                        lineHeight: 1.55,
                      }}
                    >
                      {" "}
                      {AUTHORIZATION_TEXT2}{" "}
                    </p>{" "}
                  </div>

                  {(location.pathname === "/payoff-authorization" ||
                    location.pathname === "/payoff-borrower-authorization") &&
                    location?.state?.status === "PENDING" && (
                      <div className="payoff-actions mt-5 pt-5">
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={handleCancel}
                          disabled={submitting}
                        >
                          Cancel
                        </button>

                        <LoadingButton
                          loading={submitting}
                          disabled={
                            submitting ||
                            !decision ||
                            (isAuthorizationFlow &&
                              (!authorized || signatureEmpty))
                          }
                          type="button"
                          className={"submit-payoff-btn"}
                          title={
                            submitting
                              ? "Submitting..."
                              : "Submit Payoff Request"
                          }
                          onClick={handleSubmit}
                        />
                      </div>
                    )}
                </>
              }
            />
          )}

          {/* -------------------------------- REJECTION FLOW -------------------------------- */}

          {isRejectionFlow && (
            <ActionModal
              title={"Reason for Rejection"}
              show={showRejectionModal}
              setShow={setShowRejectionModal}
              comp={
                <>
                  <div className="rejection-section">
                    <h2>Reason for Rejection</h2>

                    <p>
                      Please provide a reason for rejecting this payoff request.
                      This remark will be included with the rejection record.
                    </p>

                    <div className="rejection-field">
                      <label htmlFor="rejectionRemark">
                        Rejection Remark
                        <span className="required-star">*</span>
                      </label>

                      <textarea
                        id="rejectionRemark"
                        value={rejectionRemark}
                        onChange={(e) => setRejectionRemark(e.target.value)}
                        placeholder="Enter the reason for rejecting this payoff request..."
                        rows={5}
                        maxLength={1000}
                        disabled={submitting}
                      />

                      <div className="rejection-character-count">
                        {rejectionRemark.length}/1000
                      </div>
                    </div>
                  </div>

                  {/* -------------------------------- FOOTER -------------------------------- */}

                  {(location.pathname === "/payoff-authorization" ||
                    location.pathname === "/payoff-borrower-authorization") &&
                    location?.state?.status === "PENDING" && (
                      <div className="payoff-actions">
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={handleCancel}
                          disabled={submitting}
                        >
                          Cancel
                        </button>

                        <LoadingButton
                          loading={submitting}
                          disabled={
                            submitting ||
                            !decision ||
                            (isRejectionFlow && !rejectionRemark.trim())
                          }
                          type="button"
                          className={"reject-payoff-btn"}
                          title={
                            submitting
                              ? "Submitting..."
                              : "Reject Payoff Request"
                          }
                          onClick={handleSubmit}
                        />
                      </div>
                    )}
                </>
              }
            />
          )}
        </div>
      ) : (
        <ThankYouView countdown={countdown} />
      )}
    </div>
  );
};

export default RequestDetails;

const ThankYouView = ({ countdown }) => {
  return (
    <div className="card shadow-lg border-0 rounded-4 p-5 text-center animate-fade-in">
      <div className="py-5">
        <div className="mb-4">
          <CheckCircleFill
            size={80}
            className="text-success shadow-sm rounded-circle"
          />
        </div>
        <h2 className="fw-bold text-dark mb-2">Thank You!</h2>
        <h5 className="text-muted mb-4">
          Your signature has been submitted succeesfully!
        </h5>
        <p className="text-secondary mb-4">
          You will be redirected to the home page in{" "}
          <strong>{countdown}</strong> second{countdown !== 1 ? "s" : ""}.
        </p>
      </div>
    </div>
  );
};

const ActionModal = ({ show, setShow, comp, title }) => {
  return (
    <PopupWrapper
      title={<h4 className="p-4 font-weight-bold text-dark">{title}</h4>}
      backdrop="static"
      extraClass="width-800"
      Component={comp}
      toShow={show}
      closeButton={false}
      closeEventHandler={() => setShow(false)}
    />
  );
};
